import { Button } from "@/components/ui/button";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { cn } from "@/lib/utils";
import { Image, Upload, X } from "lucide-react";
import { useState } from "react";

interface MediaPickerProps {
    value?: string;
    onChange: (value: string) => void;
}

const mockMedia = [
    {
        id: 1,
        url: "https://via.placeholder.com/300x200",
        name: "hero-image.jpg",
        type: "image",
    },
    {
        id: 2,
        url: "https://via.placeholder.com/300x200",
        name: "about-image.jpg",
        type: "image",
    },
    {
        id: 3,
        url: "https://via.placeholder.com/300x200",
        name: "banner.jpg",
        type: "image",
    },
];

export const MediaPicker = ({ value, onChange }: MediaPickerProps) => {
    const [isOpen, setIsOpen] = useState(false);
    const [activeTab, setActiveTab] = useState("browse");
    const [selectedMedia, setSelectedMedia] = useState<string>(value || "");
    const [dragActive, setDragActive] = useState(false);

    const handleDrag = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        if (e.type === "dragenter" || e.type === "dragover") {
            setDragActive(true);
        } else if (e.type === "dragleave") {
            setDragActive(false);
        }
    };

    const handleDrop = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setDragActive(false);
        if (e.dataTransfer.files && e.dataTransfer.files[0]) {
            // Тут буде логіка завантаження файлу
            console.log(e.dataTransfer.files);
        }
    };

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files[0]) {
            // Тут буде логіка завантаження файлу
            console.log(e.target.files);
        }
    };

    const handleSelect = (url: string) => {
        setSelectedMedia(url);
        onChange(url);
        setIsOpen(false);
    };

    const handleClear = () => {
        setSelectedMedia("");
        onChange("");
    };

    return (
        <div>
            <div className="relative">
                {selectedMedia ? (
                    <div className="relative rounded-lg border bg-background p-1">
                        <img
                            src={selectedMedia}
                            alt="Selected media"
                            className="h-[200px] w-full rounded object-cover"
                        />
                        <Button
                            variant="ghost"
                            size="icon"
                            className="absolute right-2 top-2"
                            onClick={handleClear}
                        >
                            <X className="h-4 w-4" />
                        </Button>
                    </div>
                ) : (
                    <Dialog open={isOpen} onOpenChange={setIsOpen}>
                        <DialogTrigger asChild>
                            <Button
                                variant="outline"
                                className="h-[200px] w-full justify-center"
                            >
                                <Image className="mr-2 h-4 w-4" />
                                Обрати зображення
                            </Button>
                        </DialogTrigger>
                        <DialogContent className="max-w-3xl">
                            <DialogHeader>
                                <DialogTitle>Медіагалерея</DialogTitle>
                                <DialogDescription>
                                    Оберіть зображення з галереї або завантажте нове
                                </DialogDescription>
                            </DialogHeader>

                            <Tabs value={activeTab} onValueChange={setActiveTab}>
                                <TabsList className="grid w-full grid-cols-2">
                                    <TabsTrigger value="browse">Галерея</TabsTrigger>
                                    <TabsTrigger value="upload">Завантажити</TabsTrigger>
                                </TabsList>

                                <TabsContent value="browse" className="border-none p-0">
                                    <ScrollArea className="h-[400px]">
                                        <div className="grid grid-cols-3 gap-4 p-4">
                                            {mockMedia.map((media) => (
                                                <button
                                                    key={media.id}
                                                    className="group relative aspect-video overflow-hidden rounded-lg border bg-background hover:bg-accent"
                                                    onClick={() => handleSelect(media.url)}
                                                >
                                                    <img
                                                        src={media.url}
                                                        alt={media.name}
                                                        className="h-full w-full object-cover transition-transform group-hover:scale-105"
                                                    />
                                                    <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-background/80 to-background/0 p-2">
                                                        <p className="text-xs font-medium text-foreground">
                                                            {media.name}
                                                        </p>
                                                    </div>
                                                </button>
                                            ))}
                                        </div>
                                    </ScrollArea>
                                </TabsContent>

                                <TabsContent value="upload" className="border-none p-0">
                                    <div
                                        className={cn(
                                            "relative flex h-[400px] flex-col items-center justify-center rounded-lg border-2 border-dashed p-4 transition-colors",
                                            dragActive
                                                ? "border-primary bg-primary/10"
                                                : "border-muted-foreground/25"
                                        )}
                                        onDragEnter={handleDrag}
                                        onDragLeave={handleDrag}
                                        onDragOver={handleDrag}
                                        onDrop={handleDrop}
                                    >
                                        <Upload className="h-8 w-8 text-muted-foreground" />
                                        <p className="mt-2 text-sm font-medium text-muted-foreground">
                                            Перетягніть файли сюди або
                                        </p>
                                        <label
                                            htmlFor="file-upload"
                                            className="mt-2 cursor-pointer text-sm font-medium text-primary hover:underline"
                                        >
                                            оберіть на комп'ютері
                                            <Input
                                                id="file-upload"
                                                type="file"
                                                className="hidden"
                                                accept="image/*"
                                                onChange={handleFileChange}
                                            />
                                        </label>
                                        <p className="mt-2 text-xs text-muted-foreground">
                                            PNG, JPG до 10MB
                                        </p>
                                    </div>
                                </TabsContent>
                            </Tabs>

                            <DialogFooter>
                                <Button variant="outline" onClick={() => setIsOpen(false)}>
                                    Скасувати
                                </Button>
                            </DialogFooter>
                        </DialogContent>
                    </Dialog>
                )}
            </div>
        </div>
    );
}; 